<template>
  <div>
    <a-card title="产品信息">
      <a-row gutter="16">
        <a-col :span="24" style="max-width: 200px; margin-bottom: 12px;">
          <a-input v-model="searchForm.search" placeholder="编号, 名称, 备注" allowClear @pressEnter="search" />
        </a-col>
        <a-col :span="24" style="width: 100px; margin-bottom: 12px;">
          <a-button type="primary" icon="search" @click="search">查询</a-button>
        </a-col>

        <a-col :span="24" style="width: 300px; margin-bottom: 12px;">
          <a-button-group>
            <a-button icon="file-excel" @click="downloadTemplate">模板下载</a-button>
            <a-upload name="file" :showUploadList="false" :customRequest="importExcel">
              <a-button icon="upload">导入</a-button>
            </a-upload>
            <a-button icon="download" @click="exportExcel">导出</a-button>
          </a-button-group>
        </a-col>

        <div style="margin-bottom: 12px; float: right;">
          <purchase-price-toggle v-if="canViewPurchasePrice" style="margin-right: 8px;" />
          <a-button v-if="canEditGoods" type="primary" icon="plus" style="margin: 0 8px;" @click="openFormModal(form)">新增产品</a-button>
        </div>
      </a-row>

      <a-row style="margin-top: 12px;">
        <a-table size="small" :columns="columns" :dataSource="items" rowKey="id" :loading="loading" :pagination="pagination"
          @change="tableChange">
          <div slot="is_active" slot-scope="value">
            <a-tag :color="value ? 'green' : 'red'">{{value ? '激活' : '冻结'}}</a-tag>
          </div>
          <div slot="purchase_price" slot-scope="value, item" class="purchase-price-cell">
            <template v-if="value == null || value === ''">-</template>
            <template v-else-if="packRatioOf(item) > 1">
              <div>{{ toPackPrice(value, item) }}</div>
              <div class="unit-price-sub">(单价 {{ Number(value).toFixed(2) }})</div>
            </template>
            <template v-else>{{ Number(value).toFixed(2) }}</template>
          </div>
          <div slot="wholesale_price" slot-scope="value, item" class="purchase-price-cell">
            <template v-if="value == null || value === ''">-</template>
            <template v-else-if="packRatioOf(item) > 1">
              <div>{{ toPackPrice(value, item) }}</div>
              <div class="unit-price-sub">(单价 {{ Number(value).toFixed(2) }})</div>
            </template>
            <template v-else>{{ Number(value).toFixed(2) }}</template>
          </div>
          <div slot="retail_price" slot-scope="value, item" class="purchase-price-cell">
            <template v-if="value == null || value === ''">-</template>
            <template v-else-if="packRatioOf(item) > 1">
              <div>{{ toPackPrice(value, item) }}</div>
              <div class="unit-price-sub">(单价 {{ Number(value).toFixed(2) }})</div>
            </template>
            <template v-else>{{ Number(value).toFixed(2) }}</template>
          </div>
          <div slot="action" slot-scope="value, item">
            <a-button-group>
              <a-button v-if="canEditGoods" icon="edit" size="small" @click="openFormModal(item)">编辑</a-button>
              <a-popconfirm v-if="canDeleteGoods" title="确定删除吗" @confirm="destroy(item.id)">
                <a-button type="danger" icon="delete" size="small">删除</a-button>
              </a-popconfirm>
            </a-button-group>
          </div>
        </a-table>
      </a-row>
    </a-card>
    <form-modal
      v-model="visible"
      :form="targetItem"
      :classificationItems="classificationItems"
      :unitItems="unitItems"
      :warehouseItems="warehouseItems"
      @create="create"
      @update="update" />
    <a-modal v-model="importLoading" :footer="null" :maskClosable="false" :closable="false">
      <div>
        <a-spin style="margin-right: 12px;" />正在导入中, 请等待...
      </div>
    </a-modal>
  </div>
</template>

<script>
  import { mapState } from 'vuex'
  import { exportExcel } from '@/utils/excel'
  import { goodsInformationExport } from '@/api/export'
  import { goodsInformationTemplate, goodsInformationImport } from '@/api/import'
  import { goodsInformationList, goodsInformationDestroy } from '@/api/goods'
  import { goodsClassificationOption, goodsUnitOption, warehousesOption } from '@/api/option'
  import { getGoodsNumber } from '@/api/data'
  import { canDelete, canEditGoods } from '@/utils/permission'
  import { packRatioOf, toPackPrice } from '@/utils/pack'
  import purchasePriceVisibility from '@/mixins/purchasePriceVisibility'

  export default {
    name: 'Warehouse',
    mixins: [purchasePriceVisibility],
    components: {
      FormModal: () => import('./FormModal.vue'),
      PurchasePriceToggle: () => import('@/components/PurchasePriceToggle/index.vue'),
    },
    data() {
      return {
        searchForm: { search: '', page: 1, page_size: 16 },
        pagination: { current: 1, total: 0, pageSize: 16 },
        loading: false,
        items: [],
        classificationItems: [],
        unitItems: [],
        warehouseItems: [],
        visible: false,
        targetItem: {},
        form: {},
        importLoading: false,
      };
    },
    computed: {
      ...mapState({
        isManager: (state) => state.user.isManager,
        permissions: (state) => state.user.permissions || [],
      }),
      auth() {
        return { isManager: this.isManager, permissions: this.permissions }
      },
      purchasePriceAuth() {
        return this.auth
      },
      canEditGoods() {
        return canEditGoods(this.auth)
      },
      canDeleteGoods() {
        return canDelete('goods', this.auth)
      },
      columns() {
        const cols = [
          {
            title: '序号',
            dataIndex: 'index',
            key: 'index',
            customRender: (value, item, index) => index + 1,
          },
          { title: '产品编号', dataIndex: 'number', sorter: true },
          { title: '产品名称', dataIndex: 'name', sorter: true },
          { title: '分类', dataIndex: 'category_name' },
          { title: '规格', dataIndex: 'spec' },
        ]
        if (this.showPurchasePrice) {
          cols.push({
            title: '进货价',
            dataIndex: 'purchase_price',
            scopedSlots: { customRender: 'purchase_price' },
          })
        }
        cols.push(
          { title: '批发价', dataIndex: 'wholesale_price', scopedSlots: { customRender: 'wholesale_price' } },
          { title: '零售价', dataIndex: 'retail_price', scopedSlots: { customRender: 'retail_price' } },
          { title: '状态', dataIndex: 'is_active', scopedSlots: { customRender: 'is_active' } },
          { title: '备注', dataIndex: 'remark' },
          { title: '操作', dataIndex: 'action', scopedSlots: { customRender: 'action' }, width: '156px' },
        )
        return cols
      },
    },
    methods: {
      packRatioOf,
      toPackPrice,
      initialize() {
        this.list();
      },
      list() {
        this.loading = true;
        goodsInformationList(this.searchForm).then(data => {
          this.pagination.total = data.count;
          this.items = data.results;
        }).finally(() => {
          this.loading = false;
        });
      },
      create(item) {
        // this.items.splice(0, 0, item);
        this.list();
      },
      update(item) {
        this.items.splice(this.items.findIndex(i => i.id == item.id), 1, item);
      },
      search() {
        this.searchForm.page = 1;
        this.pagination.current = 1;
        this.list();
      },
      openFormModal(item) {
        if (!item.id) {
          getGoodsNumber().then(data => {
            this.targetItem = {
              ...item,
              number: data.number,
              image_items: [],
              pack_ratio: item.pack_ratio != null ? item.pack_ratio : 1,
              pack_unit_name: item.pack_unit_name || '箱',
            };
          })
        } else {
          this.targetItem = { ...item, ...{image_items: item.goods_image_items.map(item => {
            return {
              uid: item.id,
              name: item.name,
              status: 'done',
              url: item.file,
              response: {
                id: item.id
              }
            }
          })} };
        }
        goodsClassificationOption({ page_size: 999999,is_active: true }).then((resp) => {
          this.classificationItems = resp.results;
        });
        goodsUnitOption({ page_size: 999999, is_active: true }).then((resp) => {
          this.unitItems = resp.results;
        });
        warehousesOption({ page_size: 999999, is_active: true }).then((resp) => {
          if (!item.id) {
            this.warehouseItems = resp.results
          } else {
            this.warehouseItems = resp.results.map(warehouseItem => {
              let index = item.inventory_items.findIndex(_item => _item.warehouse == warehouseItem.id);
              if (index != -1) {
                warehouseItem.initial_quantity = item.inventory_items[index].initial_quantity;
                warehouseItem.batch_items = item.inventory_items[index].batch_items;
              }
              return warehouseItem
            });
          }
        });
        this.visible = true;
      },
      destroy(id) {
        goodsInformationDestroy({ id }).then(() => {
          // this.items.splice(this.items.findIndex(item => item.id == id), 1);
          this.$message.success('删除成功');
          this.list();
        });
      },
      exportExcel() {
        goodsInformationExport(this.searchForm).then(resp => {
          exportExcel(resp.data, '产品信息列表');
        }).catch(err => {
          this.$message.error(err.response.data.error);
        });
      },
      downloadTemplate () {
        goodsInformationTemplate().then(resp => {
          exportExcel(resp.data, '产品信息导入模板');
        }).catch(err => {
          this.$message.error(err.response.data.error);
        });
      },
      importExcel(item) {
        let data = new FormData();
        data.append('file', item.file);
        this.importLoading = true;
        setTimeout(() => {
          goodsInformationImport(data)
            .then(() => {
              this.$message.success('导入成功');
              this.list();
            })
            .catch(err => {
              alert("导入错误:" + "\n" + err.response.data.join("\n"));
            })
            .finally(() => {
              this.importLoading = false;
            });
        }, 1000);
      },
      tableChange(pagination, filters, sorter) {
        this.searchForm.page = pagination.current;
        this.pagination.current = pagination.current;
        this.searchForm.ordering = `${sorter.order == 'descend' ? '-' : ''}${sorter.field}`;
        this.list();
      },
    },
    mounted() {
      this.initialize();
    },
  }
</script>
<style scoped>
.purchase-price-cell {
  line-height: 1.35;
}
.unit-price-sub {
  color: #8a9a90;
  font-size: 12px;
}
</style>
